/* eslint-disable react-doctor/no-inline-exhaustive-style, react-doctor/no-tiny-text */
import Link from "next/link"
import { PlateHeading } from "@/components/plate-and-hairline/plate-heading"
import { Chrome } from "@/components/ui/tls/chrome"

interface SoundsLibraryHeaderProps {
  /** Above-the-fold lead paragraph (count-aware copy variant). */
  leadCopy: string
  /**
   * Saved-sounds state for the desktop shortlist. Omitted on the server pass
   * so the static HTML and client takeover remain stable.
   */
  saved?: { count: number; hydrated: boolean }
}

/**
 * The library header is deliberately type-led. A catalog needs its ink and
 * first rows to carry the interest; an atmospheric photo would turn the entry
 * point into a promotional card and compete with the search task.
 */
export default function SoundsLibraryHeader({ leadCopy, saved }: SoundsLibraryHeaderProps) {
  const favoriteCount = saved?.count ?? 0
  const hasHydrated = saved?.hydrated ?? false

  return (
    <header
      className="tls-clears-nav border-b border-[color:var(--tls-border-subtle)] pb-8 md:pb-12"
    >
      <div className="tls-container">
        <nav
          className="mb-6 flex items-center gap-2 font-sans text-[0.6875rem] font-semibold text-[var(--tls-fg-subtle)]"
          aria-label="Breadcrumb"
        >
          <Link
            prefetch={false}
            href="/"
            style={{ color: "var(--tls-fg-subtle)", textDecoration: "none" }}
            className="transition-colors hover:text-[color:var(--tls-fg-muted)]"
          >
            home
          </Link>
          <span aria-hidden="true" style={{ color: "var(--tls-fg-faint)" }}>/</span>
          <span style={{ color: "var(--tls-fg-muted)" }}>sounds</span>
        </nav>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_18rem] lg:items-end">
          <div>
            <div className="mb-6 flex flex-wrap items-center gap-x-3 gap-y-1">
              <Chrome>preview before you download</Chrome>
              <Link
                prefetch={false}
                href="/sounds/foryou"
                className="tls-eyebrow [--tls-eyebrow-color:var(--tls-fg-muted)] underline decoration-[var(--tls-border-strong)] underline-offset-4 transition-colors hover:[--tls-eyebrow-color:var(--tls-brand-ink)]"
              >
                For you →
              </Link>
            </div>

            <PlateHeading style={{ marginBottom: 24 }} usable={358} usableDesktop={1064}>
              Tesla Lock Sounds &amp; Chimes Library
            </PlateHeading>

            <p
              className="hidden sm:block"
              style={{
                font: "var(--tls-body-lg)",
                color: "var(--tls-fg-muted)",
                maxWidth: "56ch",
                margin: 0,
              }}
            >
              {leadCopy}
            </p>
          </div>

          <aside
            className="hidden border-t border-[color:var(--tls-border-subtle)] pt-4 lg:block"
          >
            <Chrome color="var(--tls-fg-subtle)">saved sounds</Chrome>
            <p
              style={{
                font: "var(--tls-body-sm)",
                color: "var(--tls-fg-muted)",
                margin: "8px 0 14px",
              }}
            >
              Save contenders while you browse, then come back when you are ready to install one.
            </p>
            <Link
              prefetch={false}
              href="/sounds/saved"
              className="flex min-h-[44px] items-center justify-between gap-2 border-b border-[color:var(--tls-border-subtle)] py-2 text-[var(--tls-fg-muted)] no-underline transition-[border-color,color] hover:border-[color:var(--tls-brand)] hover:text-[var(--tls-brand-ink)]"
            >
              <span className="text-sm font-semibold">Open saved sounds</span>
              {hasHydrated ? (
                <span className="font-sans text-[0.6875rem] font-semibold tabular-nums">{favoriteCount}</span>
              ) : null}
            </Link>
          </aside>
        </div>
      </div>
    </header>
  )
}
